import type { ChatAttachment } from '@shared/api';

export const MAX_AGENT_BAR_FILES = 5;
export const MAX_AGENT_BAR_FILE_BYTES = 8 * 1024 * 1024;
export const MAX_AGENT_BAR_TOTAL_BYTES = 10 * 1024 * 1024;
export const MAX_AGENT_BAR_TEXT_FILE_BYTES = 512 * 1024;

export const AGENT_BAR_FILE_ACCEPT = [
  '.pdf', '.txt', '.md', '.csv', '.tsv', '.json', '.xml', '.html',
  '.xlsx', '.xlsm', '.xls',
  '.jpg', '.jpeg', '.png', '.gif', '.webp',
].join(',');

const XLSX_MEDIA = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const MEDIA_BY_EXTENSION: Record<string, ChatAttachment['mediaType']> = {
  pdf: 'application/pdf',
  txt: 'text/plain',
  md: 'text/markdown',
  csv: 'text/csv',
  // The server converts a workbook to one CSV per sheet before the model
  // sees it (src/server/spreadsheet.ts); the browser only has to name it.
  xlsx: XLSX_MEDIA,
  xlsm: XLSX_MEDIA,
  xls: 'application/vnd.ms-excel',
  // A .tsv is tabular text the model reads exactly like a .csv — the
  // extension is the only thing that differs, and `sheet_to_csv` is never
  // involved, so it rides the text path unconverted.
  tsv: 'text/csv',
  json: 'application/json',
  xml: 'application/xml',
  html: 'text/html',
  htm: 'text/html',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
};

const ALLOWED_MEDIA_TYPES = new Set<ChatAttachment['mediaType']>(Object.values(MEDIA_BY_EXTENSION));

export interface AgentBarFile {
  id: string;
  file: File;
  mediaType: ChatAttachment['mediaType'];
}

export function agentBarFileMediaType(file: Pick<File, 'name' | 'type'>): ChatAttachment['mediaType'] | null {
  const declared = file.type.toLowerCase() as ChatAttachment['mediaType'];
  if (ALLOWED_MEDIA_TYPES.has(declared)) return declared;
  const extension = file.name.toLowerCase().split('.').pop() ?? '';
  return MEDIA_BY_EXTENSION[extension] ?? null;
}

export function addAgentBarFiles(
  existing: readonly AgentBarFile[],
  incoming: Iterable<File>,
  makeId: () => string = () => crypto.randomUUID(),
): { files: AgentBarFile[]; error: string | null } {
  const files = [...existing];
  let totalBytes = files.reduce((sum, attachment) => sum + attachment.file.size, 0);
  let error: string | null = null;

  for (const file of incoming) {
    if (files.some((attachment) => attachment.file.name === file.name && attachment.file.size === file.size && attachment.file.lastModified === file.lastModified)) {
      continue;
    }
    if (files.length >= MAX_AGENT_BAR_FILES) {
      error = `Attach up to ${MAX_AGENT_BAR_FILES} files at a time.`;
      break;
    }
    const mediaType = agentBarFileMediaType(file);
    if (!mediaType) {
      error = `${file.name} isn't supported. Add a PDF, spreadsheet (XLSX/XLS), image, text, Markdown, CSV, JSON, XML, or HTML file.`;
      continue;
    }
    if (file.size === 0) {
      error = `${file.name} is empty.`;
      continue;
    }
    if (file.size > MAX_AGENT_BAR_FILE_BYTES) {
      error = `${file.name} is larger than 8 MB.`;
      continue;
    }
    if ((mediaType.startsWith('text/') || mediaType === 'application/json' || mediaType === 'application/xml') && file.size > MAX_AGENT_BAR_TEXT_FILE_BYTES) {
      error = `${file.name} is larger than 512 KB. Text files need to be smaller so they fit in the agent context.`;
      continue;
    }
    if (totalBytes + file.size > MAX_AGENT_BAR_TOTAL_BYTES) {
      error = 'Attachments must be 10 MB or less in total.';
      continue;
    }
    files.push({ id: makeId(), file, mediaType });
    totalBytes += file.size;
  }

  return { files, error };
}

/**
 * Files on a paste. A clipboard screenshot arrives as a generic "image.png"
 * whose name and lastModified collide with the previous paste, so each one
 * gets a unique timestamped name — otherwise addAgentBarFiles' duplicate
 * check would drop a second screenshot of the same size.
 */
export function pastedAgentBarFiles(data: Pick<DataTransfer, 'files'> | null, now: () => number = Date.now): File[] {
  if (!data) return [];
  return Array.from(data.files).map((file, index) => {
    if (!file.type.startsWith('image/') || !/^image\.\w+$/i.test(file.name)) return file;
    const extension = file.name.split('.').pop()!.toLowerCase();
    const stamp = now() + index;
    return new File([file], `pasted-image-${stamp}.${extension}`, { type: file.type, lastModified: stamp });
  });
}

function bytesToBase64(bytes: Uint8Array): string {
  const chunks: string[] = [];
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    chunks.push(String.fromCharCode(...bytes.subarray(offset, offset + chunkSize)));
  }
  return btoa(chunks.join(''));
}

export async function prepareAgentBarAttachments(files: readonly AgentBarFile[]): Promise<ChatAttachment[]> {
  return Promise.all(files.map(async ({ file, mediaType }) => ({
    name: file.name,
    mediaType,
    data: bytesToBase64(new Uint8Array(await file.arrayBuffer())),
  })));
}

export function agentBarSubmissionText(text: string, files: readonly AgentBarFile[]): string {
  const trimmed = text.trim();
  if (trimmed) return trimmed;
  const names = files.map(({ file }) => file.name).join(', ');
  return `Use the attached ${files.length === 1 ? 'file' : 'files'} (${names}) as context for this proposal.`;
}

export function formatAgentBarFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
